import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { CoverageMode, DeviceModel, Placement } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	CoveragePerspectives,
	DeviceCoverageDeviceBehaviorPlugin
} from '@shure/shared/angular/features/device-coverage/data-access';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	OnboardingStage,
	OnboardingStep,
	OnboardingAction,
	DeviceCoverage3dOnboardingDeviceBehavior
} from '@shure/shared/angular/features/device-coverage-3-d/onboarding-coverage-3-d/feature-onboarding-coverage-3-d';
import { LightsSettingsSupportType } from '@shure/shared/angular/features/device-lights-settings/data-access';
import { ConfirmDialogData, ShowIcon } from '@shure/shared/angular/ui/components/dialogs/confirm';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

import { Mxa720DeviceUiDeviceBehaviorPlugin } from './mxa720-device-ui.device-behavior';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Dca720DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		DeviceCoverage3dOnboardingDeviceBehavior,
		CoverageImageProvider
{
	public readonly models = [DeviceModel.DCA7201FT, DeviceModel.DCA7202FT];

	protected readonly coverageAssetsPath: string = '';
	private readonly mxa720DeviceBehavior: Mxa720DeviceUiDeviceBehaviorPlugin;

	constructor(protected readonly transloco: TranslocoService) {
		this.mxa720DeviceBehavior = new Mxa720DeviceUiDeviceBehaviorPlugin(transloco);
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return of([
			{
				route: DeviceUiRoute.Coverage,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.coverage')
			},
			{
				route: DeviceUiRoute.Channels,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.channels')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		]);
	}

	public getDefaultRoute(_model: DeviceModel, _coverageMode?: CoverageMode | undefined): DeviceUiRoute | undefined {
		return DeviceUiRoute.Coverage;
	}

	public getCoverageImage(
		model: DeviceModel,
		perspective: CoveragePerspectives,
		placement: Placement
	): CoverageImage {
		if (model === DeviceModel.MXA7201FT || model === DeviceModel.MXA7202FT) {
			return this.withCoverageAssetsPath(
				this.mxa720DeviceBehavior.getCoverageImage(model, perspective, placement)
			);
		}

		if (perspective) {
			const image = `device-dca720-${perspective}-${placement}.svg`;
			return {
				live: this.getCoverageAssetPath(image),
				virtual: this.getCoverageAssetPath(image),
				isImageRound: false
			};
		}

		const image = 'device-dca720-back.svg';
		return {
			live: this.getCoverageAssetPath(image),
			virtual: this.getCoverageAssetPath(image),
			isImageRound: true
		};
	}

	public getOnboardingStageTitle(_model: DeviceModel, _stage: OnboardingStage | OnboardingStep): string | undefined {
		return undefined;
	}

	public getOnboardingStageDescription(model: DeviceModel, stage: OnboardingStage): string | undefined {
		switch (stage) {
			case OnboardingStage.Introduction:
				return this.transloco.translate('scDevUiSharedDevBehavior.onboarding.stages.introduction.description', {
					deviceModel: model,
					channelCount: model === DeviceModel.MXA7201FT ? 6 : model === DeviceModel.MXA7202FT ? 8 : 0
				});
			default:
				return undefined;
		}
	}

	public getOnboardingStepTitle(_model: DeviceModel, step: OnboardingStep): string | undefined {
		switch (step) {
			case OnboardingStep.LobeSnap:
				return this.transloco.translate('scDevUiSharedDevBehavior.onboarding.steps.lobe-snap.title');
			default:
				return undefined;
		}
	}
	public getOnboardingStepDescription(model: DeviceModel, step: OnboardingStep): string | undefined {
		switch (step) {
			case OnboardingStep.SceneClickAndDrag:
				return this.transloco.translate(
					'scDevUiSharedDevBehavior.onboarding.steps.scene-click-and-drag.description',
					{ deviceModel: model }
				);
			case OnboardingStep.LobeAdd:
				return this.transloco.translate('scDevUiSharedDevBehavior.onboarding.steps.lobe-add.description', {
					channelCount: model === DeviceModel.MXA7201FT ? 6 : model === DeviceModel.MXA7202FT ? 8 : 0
				});
			case OnboardingStep.LobeClickAndDrag:
				return this.transloco.translate(
					'scDevUiSharedDevBehavior.onboarding.steps.lobe-click-and-drag.description',
					{ deviceModel: model }
				);
			case OnboardingStep.LobeSnap:
				return this.transloco.translate('scDevUiSharedDevBehavior.onboarding.steps.lobe-snap.description');

			default:
				return undefined;
		}
	}

	public getOnboardingActionDescription(_model: DeviceModel, action: OnboardingAction): string | undefined {
		switch (action) {
			case OnboardingAction.LobeClickAndDrag:
				return this.transloco.translate(
					'scDevUiSharedDevBehavior.onboarding.actions.lobe-click-and-drag.description'
				);
			case OnboardingAction.LobeSnap:
				return this.transloco.translate('scDevUiSharedDevBehavior.onboarding.actions.lobe-snap.description');
			default:
				return '';
		}
	}
	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedLegacy;
	}

	public showNoiseReductionIntensityLevel(): boolean {
		return true;
	}

	public usePostGateValues(): boolean {
		return false;
	}

	public usePreGateMuteValues(): boolean {
		return true;
	}

	public supportsChannels(): boolean {
		return true;
	}

	public getEqualizerBypassConfirmDialog(): ConfirmDialogData {
		return {
			title: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-title'),
			body: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-body'),
			cancelLabel: this.transloco.translate('scDevUiSharedDevBehavior.common.buttons.cancel'),
			confirmLabel: this.transloco.translate(
				'scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-confirm-button'
			),
			doNotShowDialogId: 'turnoff-bypass-eq',
			showIcon: ShowIcon.Warning
		};
	}

	private withCoverageAssetsPath(image: CoverageImage): CoverageImage {
		return {
			...image,
			live: this.getCoverageAssetPath(image.live),
			virtual: this.getCoverageAssetPath(image.virtual)
		};
	}

	private getCoverageAssetPath(image: string): string {
		return this.coverageAssetsPath ? `${this.coverageAssetsPath}/${image}` : image;
	}
}
